import { useEffect, useState } from "react";
import { api, CLIENT_ROLES } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHead } from "../components/ui-bits";
import { toast } from "sonner";
import { ShieldCheck, LockKey, Buildings, FloppyDisk } from "@phosphor-icons/react";

export default function Settings() {
  const { user } = useAuth();
  const isClient = CLIENT_ROLES.includes(user?.role);
  const [enabled, setEnabled] = useState(false);
  const [setup, setSetup] = useState(null); // { secret, otpauth_uri }
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [biz, setBiz] = useState(null);
  const [savingBiz, setSavingBiz] = useState(false);

  const refresh = () => api.get("/auth/2fa/status").then((r) => setEnabled(r.data.enabled));
  useEffect(() => {
    refresh();
    if (isClient) api.get("/business/profile").then((r) => setBiz({
      business_name: r.data.company || "",
      business_type: r.data.business?.business_type || "",
      gstin: r.data.business?.gstin || "",
      phone: r.data.business?.phone || "",
      city: r.data.business?.city || "",
      annual_turnover: r.data.business?.annual_turnover || "",
    })).catch(() => {});
  }, [isClient]);

  const saveBiz = async (e) => {
    e.preventDefault(); setSavingBiz(true);
    try { await api.put("/business/profile", biz); toast.success("Business profile updated"); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not save"); }
    finally { setSavingBiz(false); }
  };

  const [fin, setFin] = useState(null);
  useEffect(() => {
    if (isClient) api.get("/dashboard/snapshot").then((r) => setFin({
      revenue: r.data.revenue, expenses: r.data.expenses, cash_balance: r.data.cash_balance,
      receivables: r.data.receivables, payables: r.data.payables,
      gst_payable: r.data.gst_payable, tds_payable: r.data.tds_payable, source: r.data.source,
    })).catch(() => {});
  }, [isClient]);
  const saveFin = async (e) => {
    e.preventDefault();
    const { source, ...vals } = fin;
    await api.put("/financials", vals);
    toast.success("Financials saved — dashboard updated");
    setFin({ ...fin, source: "manual" });
  };
  const importCsv = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/financials/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${Object.keys(data.imported).length} figures from ${file.name}`);
      const r = await api.get("/dashboard/snapshot");
      setFin({ revenue: r.data.revenue, expenses: r.data.expenses, cash_balance: r.data.cash_balance,
        receivables: r.data.receivables, payables: r.data.payables, gst_payable: r.data.gst_payable,
        tds_payable: r.data.tds_payable, source: r.data.source });
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
  };
  const FIN_FIELDS = [["revenue", "Revenue"], ["expenses", "Expenses"], ["cash_balance", "Cash Balance"],
    ["receivables", "Receivables"], ["payables", "Payables"], ["gst_payable", "GST Payable"], ["tds_payable", "TDS Payable"]];

  const startSetup = async () => {
    setLoading(true);
    try { setSetup((await api.post("/auth/2fa/setup")).data); }
    finally { setLoading(false); }
  };

  const enable = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/auth/2fa/enable", { code });
      toast.success("Two-Factor Authentication enabled");
      setSetup(null); setCode(""); refresh();
    } catch (err) { toast.error("Invalid code — try again"); }
    finally { setLoading(false); }
  };

  const disable = async () => {
    setLoading(true);
    try { await api.post("/auth/2fa/disable"); toast.success("2FA disabled"); refresh(); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <PageHead eyebrow="Your Profile & Security" title="Settings" />

      {isClient && biz && (
        <form onSubmit={saveBiz} className="card p-6 max-w-xl mb-6 space-y-3">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-11 h-11 rounded-sm bg-[#002FA7]/10 flex items-center justify-center"><Buildings size={22} color="#002FA7" weight="fill" /></div>
            <div><p className="font-head font-bold text-lg">Business Profile</p><p className="text-sm text-[#888]">Your details power the whole portal — keep them current.</p></div>
          </div>
          <div><label className="label block mb-1">Business Name</label>
            <input data-testid="bp-name" value={biz.business_name} onChange={(e) => setBiz({ ...biz, business_name: e.target.value })} className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label block mb-1">Type</label>
              <input data-testid="bp-type" value={biz.business_type} onChange={(e) => setBiz({ ...biz, business_type: e.target.value })} className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></div>
            <div><label className="label block mb-1">GSTIN</label>
              <input data-testid="bp-gstin" value={biz.gstin} onChange={(e) => setBiz({ ...biz, gstin: e.target.value })} className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></div>
            <div><label className="label block mb-1">Phone</label>
              <input data-testid="bp-phone" value={biz.phone} onChange={(e) => setBiz({ ...biz, phone: e.target.value })} className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></div>
            <div><label className="label block mb-1">City</label>
              <input data-testid="bp-city" value={biz.city} onChange={(e) => setBiz({ ...biz, city: e.target.value })} className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></div>
          </div>
          <div><label className="label block mb-1">Annual Turnover</label>
            <input data-testid="bp-turnover" value={biz.annual_turnover} onChange={(e) => setBiz({ ...biz, annual_turnover: e.target.value })} className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></div>
          <button data-testid="bp-save" disabled={savingBiz} className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 rounded-sm text-sm flex items-center gap-2 transition-colors disabled:opacity-60"><FloppyDisk size={16} /> {savingBiz ? "Saving…" : "Save Profile"}</button>
        </form>
      )}

      {isClient && fin && (
        <form onSubmit={saveFin} className="card p-6 max-w-xl mb-6 space-y-3">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-11 h-11 rounded-sm bg-[#00A86B]/10 flex items-center justify-center"><FloppyDisk size={22} color="#00A86B" weight="fill" /></div>
            <div>
              <p className="font-head font-bold text-lg">Financials {fin.source && <span className="text-xs font-normal text-[#888]">· source: {fin.source}</span>}</p>
              <p className="text-sm text-[#888]">Enter real figures or import a trial balance — your dashboard uses these.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {FIN_FIELDS.map(([k, label]) => (
              <div key={k}><label className="label block mb-1">{label}</label>
                <input data-testid={`fin-${k}`} type="number" value={fin[k] ?? ""} onChange={(e) => setFin({ ...fin, [k]: e.target.value === "" ? null : Number(e.target.value) })}
                  className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm tnum focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></div>
            ))}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button data-testid="fin-save" className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 rounded-sm text-sm flex items-center gap-2 transition-colors"><FloppyDisk size={16} /> Save Financials</button>
            <label data-testid="fin-import" className="border border-black/20 hover:bg-black/5 px-4 py-2 rounded-sm text-sm cursor-pointer transition-colors">
              Import trial balance (CSV / Excel)
              <input type="file" accept=".csv,.txt,.xlsx,.xlsm" hidden onChange={(e) => e.target.files[0] && importCsv(e.target.files[0])} />
            </label>
          </div>
        </form>
      )}

      <div className="card p-6 max-w-xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-sm bg-[#002FA7]/10 flex items-center justify-center"><ShieldCheck size={22} color="#002FA7" weight="fill" /></div>
          <div>
            <p className="font-head font-bold text-lg">Two-Factor Authentication</p>
            <p className="text-sm text-[#888]">Add an authenticator-app code on top of your password.</p>
          </div>
          <span className={`ml-auto text-xs px-2 py-1 rounded-sm border font-semibold ${enabled ? "bg-[#00A86B]/10 text-[#00A86B] border-[#00A86B]/30" : "bg-black/5 text-[#555] border-black/15"}`}>
            {enabled ? "ENABLED" : "DISABLED"}
          </span>
        </div>

        {enabled ? (
          <button data-testid="disable-2fa" onClick={disable} disabled={loading}
            className="border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/5 px-4 py-2 rounded-sm text-sm transition-colors">
            Disable 2FA
          </button>
        ) : setup ? (
          <form onSubmit={enable} className="space-y-4 border-t border-black/10 pt-4">
            <p className="text-sm">1. Scan this QR in Google Authenticator / Authy:</p>
            <img data-testid="twofa-qr" alt="2FA QR" className="border border-black/10 rounded-sm"
              src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(setup.otpauth_uri)}`} />
            <p className="text-xs text-[#888]">Or enter this key manually: <span className="font-mono tnum select-all">{setup.secret}</span></p>
            <p className="text-sm">2. Enter the 6-digit code to confirm:</p>
            <input data-testid="twofa-setup-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" inputMode="numeric"
              className="w-40 border border-black/20 rounded-sm px-3 py-2 tracking-[0.3em] text-center focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
            <div className="flex gap-2">
              <button data-testid="enable-2fa" disabled={loading} className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 rounded-sm text-sm transition-colors">Verify & Enable</button>
              <button type="button" onClick={() => setSetup(null)} className="border border-black/20 px-4 py-2 rounded-sm text-sm hover:bg-black/5">Cancel</button>
            </div>
          </form>
        ) : (
          <button data-testid="setup-2fa" onClick={startSetup} disabled={loading}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 rounded-sm text-sm flex items-center gap-2 transition-colors">
            <LockKey size={16} /> {loading ? "…" : "Set up 2FA"}
          </button>
        )}
      </div>
    </div>
  );
}
